import {Component, Input, OnInit} from '@angular/core';
import {QuestionOptions} from '../../../constants/question-options';
import {FormBuilder, FormControl, Validators} from '@angular/forms';
import {CustomValidators} from '../../../validator/custom-validator';
import {ProposalFormBase} from '../proposal-form-base/proposal-form-base.component';
import {Proposal} from 'src/app/model/proposal/proposal';
import {LineOfBusiness} from '../../../model/proposal/line-of-business';
import {formatDate} from '@angular/common';
import {TranslateService} from '@ngx-translate/core';
import {ReplaySubject, takeUntil} from 'rxjs';
import {Store} from '@ngrx/store';
import {selectUserLobsOfferingNumber} from '../../../store/user/selectors/user.selectors';

@Component({
    selector: 'app-risk-data-form',
    templateUrl: './risk-data-form.component.html',
    styleUrls: ['../../../../styles/form-input.scss'],
    providers: [{ provide: ProposalFormBase, useExisting: RiskDataFormComponent }],
    standalone: false
})

export class RiskDataFormComponent extends ProposalFormBase implements OnInit {

  @Input() proposalPreview: Proposal;

  duration: number = null;
  months: string[];
  QuestionOptions = QuestionOptions;
  public filterCtrlLineOfBusiness: FormControl<string> = new FormControl<string>('');
  public filteredOptionsLineOfBusiness: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);

  linesOfBusiness: string[] = [];
  formGroup = this.fb.group({
      coveragePercentageRequested: new FormControl<number>(100, [Validators.required, CustomValidators.validNumber()]),
      riskDateBegin: new FormControl<string>(null, [Validators.required]),
      riskDateEnd: new FormControl<string>(null, [Validators.required]),
      leadInsurerNeeded: new FormControl<boolean>(true),
      followingInsurerNeeded: new FormControl<boolean>(true),
      mainDueDate: [''],
      paymentMethod: [''],
      lineOfBusiness: ['', [Validators.required]]
    },
    {
      validators: [
        CustomValidators.firstDataBeforeSecond('riskDateBegin', 'riskDateEnd'),
        CustomValidators.atLeastOneTrue(
          'leadInsurerNeeded',
          'followingInsurerNeeded'
        )]
    });

  offeringLobsNumber$ = this.store.select(selectUserLobsOfferingNumber);

  constructor(private readonly fb: FormBuilder, private readonly store: Store, private readonly translate: TranslateService) {
    super();
    this.months = [];
    for (let i = 1; i < 13; i++) {
      this.months.push(String(i));
    }


  }

  ngOnInit() {
    const coverageCtrl = this.formGroup.get('coveragePercentageRequested');
    const leadCtrl = this.formGroup.get('leadInsurerNeeded');
    const followCtrl = this.formGroup.get('followingInsurerNeeded');

    // resets values to true of lead and follow if coverage is >= 100
    coverageCtrl?.valueChanges.subscribe(value => {
      if (value >= 100) {
        leadCtrl?.setValue(true, { emitEvent: false });
        followCtrl?.setValue(true, { emitEvent: false });
      }
    });

    if (this.proposalPreview != null) {
      if (this.proposalPreview.proposalInformation?.coveragePercentageRequested < 100) {
        this.formGroup.controls.coveragePercentageRequested.patchValue(this.proposalPreview.proposalInformation?.coveragePercentageRequested);
        this.formGroup.controls.leadInsurerNeeded.patchValue(this.proposalPreview.proposalInformation?.leadInsurerNeeded);
        this.formGroup.controls.followingInsurerNeeded.patchValue(this.proposalPreview.proposalInformation?.followingInsurerNeeded);
      }
      if (this.proposalPreview.proposalInformation?.riskDateBegin.getFullYear() !== 1970) {
        this.formGroup.controls.riskDateBegin.patchValue(formatDate(new Date(this.proposalPreview.proposalInformation?.riskDateBegin), 'yyyy-MM-dd', 'en'));
      }
      if (this.proposalPreview.proposalInformation?.riskDateEnd.getFullYear() !== 1970) {
        this.formGroup.controls.riskDateEnd.patchValue(formatDate(new Date(this.proposalPreview.proposalInformation?.riskDateEnd), 'yyyy-MM-dd', 'en'));
      }
      this.formGroup.controls.mainDueDate.patchValue(this.proposalPreview.proposalInformation?.mainDueDate);
      this.formGroup.controls.paymentMethod.patchValue(this.proposalPreview.proposalInformation?.paymentMethod);
      this.formGroup.controls.lineOfBusiness.patchValue(this.proposalPreview.lineOfBusiness?.number);
    }
    this.linesOfBusiness = QuestionOptions.lineOfBusiness;
    this.offeringLobsNumber$.subscribe(list => {
      list.sort((a, b) => parseFloat(a) - parseFloat(b));
      this.filteredOptionsLineOfBusiness.next(list.slice());
      this.filterCtrlLineOfBusiness.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.filterLineOfBusiness(this.filterCtrlLineOfBusiness.value, list, this.filteredOptionsLineOfBusiness);
        });
    })
  }

  filterLineOfBusiness(search: string, options, filtered) {
    if (!options) {
      return;
    }
    if (!search) {
      filtered.next(options.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    filtered.next(
      options.filter(lineOfBusiness => this.translateLineOfBusiness(lineOfBusiness).toLowerCase().indexOf(search) > -1)
    );
  }

  private translateLineOfBusiness(lineOfBusiness): string {
    return lineOfBusiness + '-' + this.translate.instant('lineOfBusiness.clauses.' + lineOfBusiness + '.name');

  }

  updateProposal(proposal: Proposal): void {
    proposal.lineOfBusiness = new LineOfBusiness();
    //FOR CODE REVIREW: raw value instead of value, because now we have disabled fields and could not be read by "value"
    const rawValue = this.formGroup.getRawValue();
    proposal.lineOfBusiness.number = rawValue.lineOfBusiness;
    proposal.proposalInformation.update(rawValue);
  }

  autoFillForTransmission(proposal: Proposal): void {
    if (proposal.lineOfBusiness != null) {
      this.formGroup.controls.lineOfBusiness.patchValue(proposal.lineOfBusiness.number);
    }
    if (proposal.proposalInformation != null) {
      this.formGroup.controls.mainDueDate.patchValue(proposal.proposalInformation.mainDueDate);
      this.formGroup.controls.riskDateBegin.patchValue(formatDate(new Date(proposal.proposalInformation.riskDateBegin), 'yyyy-MM-dd', 'en'));
      this.formGroup.controls.riskDateEnd.patchValue(formatDate(new Date(proposal.proposalInformation.riskDateEnd), 'yyyy-MM-dd', 'en'));
      this.formGroup.controls.paymentMethod.patchValue(proposal.proposalInformation.paymentMethod);
    }
  }

  autoFillForEdit(proposal: Proposal) {
    if(proposal.proposalInformation != null) {
      this.formGroup.controls.coveragePercentageRequested.patchValue(proposal.proposalInformation.coveragePercentageRequested);
      this.formGroup.controls.coveragePercentageRequested.disable();

      this.formGroup.controls.leadInsurerNeeded.patchValue(proposal.proposalInformation.leadInsurerNeeded);
      this.formGroup.controls.leadInsurerNeeded.disable();

      this.formGroup.controls.followingInsurerNeeded.patchValue(proposal.proposalInformation.followingInsurerNeeded);
      this.formGroup.controls.followingInsurerNeeded.disable();

      this.formGroup.controls.mainDueDate.patchValue(proposal.proposalInformation.mainDueDate);
      this.formGroup.controls.riskDateBegin.patchValue(formatDate(new Date(proposal.proposalInformation.riskDateBegin), 'yyyy-MM-dd', 'en'));
      this.formGroup.controls.riskDateEnd.patchValue(formatDate(new Date(proposal.proposalInformation.riskDateEnd), 'yyyy-MM-dd', 'en'));
      this.formGroup.controls.paymentMethod.patchValue(proposal.proposalInformation.paymentMethod);
    }
    if (proposal.lineOfBusiness != null) {
      this.formGroup.controls.lineOfBusiness.patchValue(proposal.lineOfBusiness.number);
      this.formGroup.controls.lineOfBusiness.disable();
    }
  }

  dateBeforeValidation() {
    const touched = (this.formGroup.controls.riskDateBegin.touched || this.formGroup.controls.riskDateBegin.dirty)
      && (this.formGroup.controls.riskDateEnd.touched || this.formGroup.controls.riskDateEnd.dirty);
    return touched && this.formGroup.hasError('dateBefore');
  }

  calculateDurationInMonths() {
    if (this.formGroup.value.riskDateBegin == null || this.formGroup.value.riskDateEnd == null || (this.formGroup.value.riskDateBegin > this.formGroup.value.riskDateEnd)) {
      this.duration = null;
    } else {
      let begin = new Date(this.formGroup.value.riskDateBegin);
      let end = new Date(this.formGroup.value.riskDateEnd);
      this.duration =
        end.getMonth() -
        begin.getMonth() +
        12 * (end.getFullYear() - begin.getFullYear());
    }
  }

  containsStringFromArray(mainString: string, stringArray: string[]): boolean {
    for (const searchString of stringArray) {
      if (mainString.includes(searchString)) {
        return true;
      }
    }
    return false;
  }

  reset() {
    this.formGroup.reset();
    this.formGroup.markAsPristine();
    this.formGroup.markAsUntouched();
  }

  onRoleChange(changed: 'leadInsurerNeeded' | 'followingInsurerNeeded') {
    const lead = this.formGroup.get('leadInsurerNeeded')!;
    const follow = this.formGroup.get('followingInsurerNeeded')!;

    if (!lead.value && !follow.value) {
      this.formGroup.get(changed)?.setValue(true, { emitEvent: false });
    }
  }
}
